import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import EmotionToggle from '../ui/EmotionToggle';
import { Button } from '../ui/button';
import ShareDialog from '../ui/ShareDialog';
import { ArrowUp, ArrowLeft, LayoutGrid, List } from 'lucide-react';
import { fetchCards, deleteCard } from "../../services/cardService";
import Masonry from 'react-masonry-css';
import DOMPurify from 'dompurify';
import PDFViewer from '../PDFViewer';

const gradients = {
    white: 'bg-white',
    sunset: 'bg-gradient-to-r from-orange-400 to-pink-500',
    ocean: 'bg-gradient-to-r from-blue-400 to-green-400',
    forest: 'bg-gradient-to-r from-green-400 to-teal-500',
    lavender: 'bg-gradient-to-r from-purple-400 to-pink-300',
    golden: 'bg-gradient-to-r from-yellow-400 to-orange-400',
    midnight: 'bg-gradient-to-r from-blue-900 to-purple-800',
    spring: 'bg-gradient-to-r from-green-200 to-yellow-200',
    autumn: 'bg-gradient-to-r from-red-500 to-yellow-400'
};

const gradientColors = {
    white: { start: '#ffffff', end: '#ffffff' },
    sunset: { start: '#f97316', end: '#ec4899' },
    ocean: { start: '#60a5fa', end: '#34d399' },
    forest: { start: '#4ade80', end: '#14b8a6' },
    lavender: { start: '#a78bfa', end: '#f472b6' },
    golden: { start: '#facc15', end: '#f97316' },
    midnight: { start: '#1e3a8a', end: '#581c87' },
    spring: { start: '#86efac', end: '#fde047' },
    autumn: { start: '#ef4444', end: '#facc15' }
};

function extractYouTubeId(url) {
    if (!url) return null;
    const patterns = [
        /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^?\s]+)/,
        /(?:youtube\.com\/embed\/)([^?\s]+)/,
        /(?:youtube\.com\/shorts\/)([^?\s]+)/
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
    }
    return null;
}

function getDisplayUrl(url) {
    try { return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, ''); } catch { return url; }
}

function formatDate(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    }) + ' · ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true
    });
}

function isLightCard(card) {
    return card.gradient === 'white' || card.gradient === 'spring';
}

function renderMessage(msg, className) {
    if (!msg) return null;
    if (msg.includes('<')) {
        return <div className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg) }} />;
    }
    return <p className={`${className} whitespace-pre-line`}>{msg}</p>;
}

const useFirestoreLikes = false;
const breakpointColumnsObj = {
    default: 3,
    1024: 2,
    640: 1
};




export const WallOfEmotions = ({ onPost, onEdit, user, onSignIn, onSignOut }) => {
    const [cards, setCards] = useState([]);
    const [reacted, setReacted] = useState({});
    const [selectedCard, setSelectedCard] = useState(null);
    const [youtubeVideoId, setYoutubeVideoId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('viewMode') || 'feed');
    const [showScrollTop, setShowScrollTop] = useState(false);
    const navigate = useNavigate();
    const cachedCards = JSON.parse(localStorage.getItem("wallCards") || "[]");
    const getGradientStyle = (card) => {
        if (card.gradient === 'white') {
            return {
                background: '#ffffff',
                color: card.textColor || '#000000'
            };
        }
        if (card.gradient === 'custom' && card.customStartColor && card.customEndColor) {
            return {
                background: `linear-gradient(to right, ${card.customStartColor}, ${card.customEndColor})`,
                color: card.textColor
            };
        } else if (card.gradient && gradientColors[card.gradient]) {
            return {
                background: `linear-gradient(to right, ${gradientColors[card.gradient].start}, ${gradientColors[card.gradient].end})`,
                color: card.textColor
            };
        }
        return { color: card.textColor };
    };



    useEffect(() => {

        const loadCards = async () => {
            const firestoreCards = await fetchCards();
            const localMap = cachedCards.reduce((acc, card) => {
                acc[card.id] = card;
                return acc;
            }, {});
            const mergedCards = firestoreCards.map(card => ({
                ...card,
                appreciateCount: localMap[card.id]?.appreciateCount || card.appreciateCount || 0
            }));
            setCards(mergedCards);
        };
        loadCards();
    }, []);

    useEffect(() => {
        document.body.style.overflow = (selectedCard || youtubeVideoId) ? 'hidden' : 'auto';
    }, [selectedCard, youtubeVideoId]);

    useEffect(() => {
        if (!useFirestoreLikes) {
            const savedReactions = JSON.parse(localStorage.getItem("reactedCards") || "{}");
            setReacted(savedReactions);
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setSelectedCard(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);




    useEffect(() => {
        localStorage.setItem('viewMode', viewMode);
    }, [viewMode]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY;
            const clientHeight = window.innerHeight;
            const scrollPercent = scrollTop / (scrollHeight - clientHeight);
            setShowScrollTop(scrollPercent > 0.8);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleAppreciate = (id) => {
        confetti({ particleCount: 60, spread: 90, origin: { y: 0.6 } });
    };

    const handleDelete = async (cardId) => {
        if (!window.confirm("Are you sure you want to delete this card?")) return;
        const success = await deleteCard(cardId);
        if (success) {
            setCards(prev => prev.filter(c => c.id !== cardId));
        } else {
            alert("Failed to delete card. Please try again.");
        }
    };

    const filteredCards = cards.filter(card => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        const message = (card.message || '').replace(/<[^>]*>/g, '').toLowerCase();
        const url = (card.url || '').toLowerCase();
        return message.includes(q) || url.includes(q);
    });

    return (
        <>
            <style>{`
                .card-rich-content h1 { font-size: 1.5em; font-weight: bold; margin: 0.3em 0; }
                .card-rich-content h2 { font-size: 1.25em; font-weight: bold; margin: 0.2em 0; }
                .card-rich-content h3 { font-size: 1.1em; font-weight: bold; margin: 0.15em 0; }
                .card-rich-content blockquote { border-left: 3px solid currentColor; padding-left: 10px; opacity: 0.8; margin: 0.3em 0; }
                .card-rich-content ul { list-style: disc; padding-left: 20px; margin: 0.3em 0; }
                .card-rich-content ol { list-style: decimal; padding-left: 20px; margin: 0.3em 0; }
                .card-rich-content a { text-decoration: underline; }
                .card-rich-content p { margin: 0.15em 0; }
                .card-rich-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 4px 0; }
            `}</style>
            <div className="w-full px-4 sm:px-8 max-w-6xl mx-auto mt-10 pb-16 md:pb-0">

                <div className="flex items-center justify-between mt-6 mb-4">
                    <div className="hidden md:block w-24"></div>
                    <div className="text-center flex-1 md:flex-none">
                        <h2 className="text-2xl sm:text-3xl font-semibold bg-gradient-to-r from-orange-500 to-pink-500 text-transparent bg-clip-text">
                            Thoughts
                        </h2>
                        <p className="text-sm text-gray-400 mt-0.5">Thoughts manufacture energy.</p>
                    </div>
                    <div className="hidden md:flex items-center gap-2">
                        {user ? (
                            <>
                                <button
                                    onClick={onSignOut}
                                    className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                                    title="Sign out"
                                >
                                    <img
                                        src={user.photoURL}
                                        alt=""
                                        className="w-7 h-7 rounded-full border border-gray-200"
                                        referrerPolicy="no-referrer"
                                    />
                                    <span className="text-xs text-gray-600 hidden sm:inline">
                                        {user.displayName?.split(' ')[0]}
                                    </span>
                                </button>
                                <button
                                    onClick={onPost}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-medium shadow hover:shadow-lg hover:scale-105 transition-all"
                                >
                                    ✏️ Post
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={onSignIn}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-gray-300 text-gray-700 text-sm font-medium shadow hover:shadow-lg hover:scale-105 transition-all"
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Sign in
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="🔍 Search cards..."
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white/80"
                    />
                    <div className="flex rounded-lg bg-gray-100 p-0.5">
                        <button
                            onClick={() => setViewMode('masonry')}
                            className={`p-1.5 rounded-md transition ${viewMode === 'masonry' ? 'bg-white shadow text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Grid view"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('feed')}
                            className={`p-1.5 rounded-md transition ${viewMode === 'feed' ? 'bg-white shadow text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
                            title="Feed view"
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {filteredCards.length === 0 ? (
                    <p className="text-center text-gray-500">{searchQuery.trim() ? 'No cards found.' : 'No published cards yet.'}</p>
                ) : viewMode === 'masonry' ? (

                    <Masonry
                        breakpointCols={breakpointColumnsObj}
                        className="my-masonry-grid"
                        columnClassName="my-masonry-grid_column"
                    >
                        {filteredCards.map((card) => (
                            <div key={card.id} className="w-full">
                                <div
                                    className={`relative w-full max-w-md p-4 md:p-6 rounded-2xl shadow-md cursor-pointer ${gradients[card.gradient] || ''} ${card.gradient === 'white' ? 'border-2 border-gray-200' : 'border'}`}
                                    style={getGradientStyle(card)}
                                    onClick={() => setSelectedCard(card)}
                                >
                                    <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
                                        {card.url && (
                                            <a
                                                href={card.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className={`${isLightCard(card) ? 'text-gray-500 hover:text-gray-700' : 'text-white/60 hover:text-white'} transition-opacity text-xs`}
                                                title="Open link"
                                            >
                                                🔗
                                            </a>
                                        )}
                                        {user && user.uid === card.authorId && (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onEdit(card); }}
                                                    className={`${isLightCard(card) ? 'text-gray-500 hover:text-gray-700' : 'text-white/60 hover:text-white'} transition-opacity text-xs`}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(card.id); }}
                                                    className={`${isLightCard(card) ? 'text-gray-500 hover:text-gray-700' : 'text-white/60 hover:text-white'} transition-opacity text-xs`}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {card.mode === 'Classic' && (
                                        <p className="text-xl font-bold mb-2">Dear {card.recipient}</p>
                                    )}
                                    {card.mode === 'Classic' && card.emotion && (
                                        <p className="italic text-base mb-2">Expressing {card.emotion}</p>
                                    )}
                                    {renderMessage(card.message, `text-lg leading-relaxed mb-4 card-rich-content ${card.url && extractYouTubeId(card.url) ? 'line-clamp-4' : ''}`)}
                                    {card.mode === 'Classic' && (
                                        <>
                                            <p className="text-right text-base font-bold">Yours,</p>
                                            <p className="text-right text-base font-bold">{card.sender}</p>
                                        </>
                                    )}

                                    {card.url && extractYouTubeId(card.url) && (
                                        <div
                                            className="relative mt-2 mb-2 cursor-pointer rounded-lg overflow-hidden"
                                            style={{ height: '120px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setYoutubeVideoId(extractYouTubeId(card.url));
                                            }}
                                        >
                                            <img
                                                src={`https://img.youtube.com/vi/${extractYouTubeId(card.url)}/hqdefault.jpg`}
                                                alt="YouTube thumbnail"
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                <span className="text-white text-4xl">▶</span>
                                            </div>
                                        </div>
                                    )}

                                    {card.url && !extractYouTubeId(card.url) && (
                                        <a
                                            href={card.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className={`${isLightCard(card) ? 'text-blue-600' : 'text-white/80'} text-sm hover:underline flex items-center gap-1 mb-2 max-w-full`}
                                        >
                                            🔗 <span className="truncate">{getDisplayUrl(card.url)}</span>
                                        </a>
                                    )}

                                    {(() => {
                                        const pdfAtt = card.attachments?.find(a => a.type?.includes('pdf'));
                                        const nonPdfAtts = card.attachments?.filter(a => !a.type?.includes('pdf')) || [];
                                        return (
                                            <>
                                                {pdfAtt && (
                                                    <div className="mb-2 rounded-lg overflow-hidden" style={{ maxHeight: '180px' }}>
                                                        <PDFViewer url={pdfAtt.url} name={pdfAtt.name} compact />
                                                    </div>
                                                )}
                                                {nonPdfAtts.length > 0 && (
                                                    <div className={`text-xs flex items-center gap-1 mb-2 ${isLightCard(card) ? 'text-gray-500' : 'text-white/70'}`}>
                                                        <span>📎</span>
                                                        <span>{nonPdfAtts.length === 1 ? nonPdfAtts[0].name : `${nonPdfAtts.length} files`}</span>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}

                                    <div className="mt-4 flex justify-between items-center">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAppreciate(card.id);
                                            }}
                                        >
                                            ❤️
                                        </Button>

                                        {card.createdAt ? (
                                            <span className={`text-xs opacity-50 ${card.gradient === 'white' ? 'text-black' : 'text-white'}`}>
                                                {card.authorName ? `By ${card.authorName.split(' ')[0]} · ` : ''}{formatDate(card.createdAt)}
                                            </span>
                                        ) : <span />}

                                        <div onClick={(e) => e.stopPropagation()}>
                                            <ShareDialog
                                                selectedEmotion={card.emotionLabel || card.emotion}
                                                cardUrl="https://express.arake.app/wall"
                                                iconColor={
                                                    ['sunset', 'ocean', 'forest', 'lavender', 'midnight'].includes(card.gradient)
                                                        ? 'white'
                                                        : 'black'
                                                }
                                            />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </Masonry>

                ) : (
                    <div className="max-w-[600px] mx-auto space-y-3 px-1 md:px-0">
                        {filteredCards.map((card) => (
                            <div
                                key={card.id}
                                className={`rounded-xl overflow-hidden cursor-pointer ${card.gradient === 'white' ? 'border-2 border-gray-200' : 'border border-gray-100'}`}
                                style={getGradientStyle(card)}
                                onClick={() => setSelectedCard(card)}
                            >
                                <div className="flex items-center justify-between px-4 pt-3 pb-1">
                                    <div className="flex items-center gap-2">
                                        {card.authorPhoto && (
                                            <img
                                                src={card.authorPhoto}
                                                alt=""
                                                className="w-6 h-6 rounded-full"
                                                referrerPolicy="no-referrer"
                                            />
                                        )}
                                        <span className={`text-xs ${isLightCard(card) ? 'text-gray-500' : 'opacity-60'}`}>
                                            {card.authorName ? card.authorName.split(' ')[0] : ''}
                                            {card.authorName && card.createdAt ? ' · ' : ''}
                                            {card.createdAt ? formatDate(card.createdAt) : ''}
                                        </span>
                                    </div>
                                    {user && user.uid === card.authorId && (
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onEdit(card); }}
                                                className={`${isLightCard(card) ? 'text-gray-500 hover:text-gray-700' : 'opacity-60 hover:opacity-100'} transition-opacity text-xs`}
                                                title="Edit"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(card.id); }}
                                                className={`${isLightCard(card) ? 'text-gray-500 hover:text-gray-700' : 'opacity-60 hover:opacity-100'} transition-opacity text-xs`}
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {card.mode === 'Classic' && (
                                    <div className="px-4 pt-1">
                                        <p className="text-sm font-bold">Dear {card.recipient}</p>
                                        {card.emotion && (
                                            <p className="italic text-xs mt-0.5">Expressing {card.emotion}</p>
                                        )}
                                    </div>
                                )}

                                <div className="px-4 py-2">
                                    {renderMessage(card.message, "text-sm leading-relaxed line-clamp-3 card-rich-content")}
                                    {card.message && card.message.length > 150 && (
                                        <span className="text-xs text-orange-500 mt-1 inline-block">Read more</span>
                                    )}
                                </div>

                                {card.mode === 'Classic' && (
                                    <div className="px-4 pb-1 text-right">
                                        <p className="text-xs font-bold">Yours,</p>
                                        <p className="text-xs font-bold">{card.sender}</p>
                                    </div>
                                )}

                                {card.url && extractYouTubeId(card.url) && (
                                    <div className="px-4 pb-2">
                                        <div
                                            className="relative rounded-lg overflow-hidden cursor-pointer"
                                            style={{ height: '160px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setYoutubeVideoId(extractYouTubeId(card.url));
                                            }}
                                        >
                                            <img
                                                src={`https://img.youtube.com/vi/${extractYouTubeId(card.url)}/hqdefault.jpg`}
                                                alt="YouTube thumbnail"
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                <span className="text-white text-3xl">▶</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {card.url && !extractYouTubeId(card.url) && (
                                    <a
                                        href={card.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className={`px-4 pb-2 text-xs hover:underline flex items-center gap-1 max-w-full ${isLightCard(card) ? 'text-blue-600' : 'opacity-70'}`}
                                    >
                                        🔗 <span className="truncate">{getDisplayUrl(card.url)}</span>
                                    </a>
                                )}

                                {(() => {
                                    const pdfAtt = card.attachments?.find(a => a.type?.includes('pdf'));
                                    const nonPdfAtts = card.attachments?.filter(a => !a.type?.includes('pdf')) || [];
                                    return (
                                        <>
                                            {pdfAtt && (
                                                <div className="px-4 mb-2 rounded-lg overflow-hidden" style={{ maxHeight: '180px' }}>
                                                    <PDFViewer url={pdfAtt.url} name={pdfAtt.name} compact />
                                                </div>
                                            )}
                                            {nonPdfAtts.length > 0 && (
                                                <div className={`px-4 pb-1 text-xs flex items-center gap-1 ${isLightCard(card) ? 'text-gray-500' : 'opacity-70'}`}>
                                                    <span>📎</span>
                                                    <span>{nonPdfAtts.length === 1 ? nonPdfAtts[0].name : `${nonPdfAtts.length} files`}</span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                <div className="px-4 pb-3 flex justify-between items-center border-t border-black/5 pt-2 mt-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppreciate(card.id);
                                        }}
                                    >
                                        ❤️
                                    </Button>

                                    <div onClick={(e) => e.stopPropagation()}>
                                        <ShareDialog
                                            selectedEmotion={card.emotionLabel || card.emotion}
                                            cardUrl="https://express.arake.app/wall"
                                            iconColor={
                                                ['sunset', 'ocean', 'forest', 'lavender', 'midnight'].includes(card.gradient)
                                                    ? 'white'
                                                    : 'black'
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {selectedCard && (
                    <div
                        id="emotion-modal"
                        className="fixed inset-0 bg-black/30 backdrop-blur-lg flex items-center justify-center z-50"
                        onClick={() => setSelectedCard(null)}
                        tabIndex={-1}
                    >

                        <div
                            className="p-4 md:p-6 rounded-t-2xl md:rounded-2xl shadow-lg max-w-2xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto relative mx-0 md:mx-4"
                            style={getGradientStyle(selectedCard)}
                        >

                            {/* Close button */}
                            <button
                                className="absolute top-2 right-2 text-black text-2xl md:text-xl font-bold hover:text-red-500 w-10 h-10 flex items-center justify-center"
                                onClick={() => setSelectedCard(null)}
                            >
                                ×
                            </button>

                            {/* Text content */}
                            {selectedCard.mode === 'Classic' && (
                                <>
                                    <p className="text-xl font-bold mb-2">Dear {selectedCard.recipient}</p>
                                    {selectedCard.emotion && (
                                        <p className="italic text-base mb-2">Expressing {selectedCard.emotion}</p>
                                    )}
                                </>
                            )}
                            {renderMessage(selectedCard.message, "text-lg leading-relaxed mb-4 card-rich-content")}
                            {selectedCard.mode === 'Classic' && (
                                <>
                                    <p className="text-right text-base font-bold">Yours,</p>
                                    <p className="text-right text-base font-bold">{selectedCard.sender}</p>
                                </>
                            )}

                            {selectedCard.url && extractYouTubeId(selectedCard.url) && (
                                <div
                                    className="relative mt-2 mb-2 cursor-pointer rounded-lg overflow-hidden"
                                    style={{ height: '180px' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setYoutubeVideoId(extractYouTubeId(selectedCard.url));
                                    }}
                                >
                                    <img
                                        src={`https://img.youtube.com/vi/${extractYouTubeId(selectedCard.url)}/hqdefault.jpg`}
                                        alt="YouTube thumbnail"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                        <span className="text-white text-4xl">▶</span>
                                    </div>
                                </div>
                            )}

                            {selectedCard.url && !extractYouTubeId(selectedCard.url) && (
                                <a
                                    href={selectedCard.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className={`${isLightCard(selectedCard) ? 'text-blue-600' : 'text-white/80'} text-sm hover:underline flex items-center gap-1 mb-2 max-w-full`}
                                >
                                    🔗 <span className="truncate">{getDisplayUrl(selectedCard.url)}</span>
                                </a>
                            )}

                            {selectedCard.attachments?.length > 0 && (
                                <div className="mt-2 mb-2 space-y-3">
                                    {selectedCard.attachments.filter(a => a.type?.includes('pdf')).map((att, i) => (
                                        <div key={`pdf-${i}`} className="mb-3">
                                            <PDFViewer url={att.url} name={att.name} compact={false} />
                                        </div>
                                    ))}
                                    {selectedCard.attachments.filter(a => !a.type?.includes('pdf')).map((att, i) => (
                                        <a
                                            key={`file-${i}`}
                                            href={att.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-2 text-sm p-2 rounded transition ${isLightCard(selectedCard) ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span>📝</span>
                                            <span className="truncate flex-1">{att.name}</span>
                                            <span className={`text-xs ${isLightCard(selectedCard) ? 'text-gray-400' : 'opacity-50'}`}>{(att.size / 1024).toFixed(0)} KB</span>
                                            <span className="text-blue-500">↓</span>
                                        </a>
                                    ))}
                                </div>
                            )}

                            {/* Bottom buttons */}
                            <div className="mt-4 flex justify-between items-center">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAppreciate(selectedCard.id);
                                    }}
                                >
                                    ❤️
                                </Button>

                                {selectedCard.createdAt ? (
                                    <span className={`text-xs opacity-50 ${isLightCard(selectedCard) ? 'text-black' : 'text-white'}`}>
                                        {selectedCard.authorName ? `By ${selectedCard.authorName.split(' ')[0]} · ` : ''}{formatDate(selectedCard.createdAt)}
                                    </span>
                                ) : <span />}

                                <div onClick={(e) => e.stopPropagation()}>
                                    <ShareDialog
                                        selectedEmotion={selectedCard.emotionLabel || selectedCard.emotion}
                                        cardUrl="https://express.arake.app/wall"
                                        iconColor={
                                            ['sunset', 'ocean', 'forest', 'lavender', 'midnight'].includes(selectedCard.gradient)
                                                ? 'white'
                                                : 'black'
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div >

            {youtubeVideoId && (
                <div
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-50"
                    onClick={() => setYoutubeVideoId(null)}
                >
                    <button
                        className="text-white text-2xl mb-2 hover:text-gray-300"
                        onClick={() => setYoutubeVideoId(null)}
                    >
                        ✕
                    </button>
                    <div
                        className="w-full max-w-3xl aspect-video px-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`}
                            className="w-full h-full rounded-lg"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                            title="YouTube video"
                        />
                    </div>
                    <a
                        href={`https://www.youtube.com/watch?v=${youtubeVideoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white text-sm mt-3 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Open in YouTube →
                    </a>
                </div>
            )}

            <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`fixed bottom-20 md:bottom-8 right-4 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-opacity duration-300 z-50 ${showScrollTop ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                size="icon"
                aria-label="Back to Top"
            >
                <ArrowUp className="w-5 h-5" />
            </Button>

            {/* Mobile bottom bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 flex items-center justify-between z-40 md:hidden">
                <div className="text-xs text-gray-400">&copy; 2026 Thoughts</div>
                {user ? (
                    <div className="flex items-center gap-2">
                        <img src={user.photoURL} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
                        <button onClick={onSignOut} className="text-sm text-gray-600 hover:text-red-500">
                            Sign out
                        </button>
                    </div>
                ) : (
                    <button onClick={onSignIn} className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-full px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" className="w-4 h-4" />
                        Sign in
                    </button>
                )}
            </div>

        </>

    );
};
